import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import {
  disableBodyScroll,
  clearAllBodyScrollLocks,
  enableBodyScroll,
} from 'body-scroll-lock';
import find from 'lodash/find';
import uniqBy from 'lodash/uniqBy';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { requestWidgetData } from 'services/actions/socialEcommActions';
import Button from 'shared-components/Button/Button';
import { VIEW_SIZE } from 'utils/device';
import { track, btnLocationEnum } from 'utils/segment';
import { trackInviteStarted } from 'utils/social-ecomm';
import { selectPageTrackingFullData } from 'selectors/trackingSelector';

import WidgetSection from './WidgetSection/WidgetSection';
import styles from './TeamWidget.module.css';

const TeamWidget = ({
  userId,
  viewSize,
  getWidgetData,
  redeemedMembers,
  socialAvatars,
  captainId,
  loading,
  trackingData,
  socialBannerIsInView,
  openInviteModal,
}) => {
  const containerRef = useRef();
  const itemsContainerRef = useRef();
  const [widgetOpen, setWidgetOpen] = useState(false);
  const { socialEcomm: socialEcommFF, disableAvatars: disableAvatarsFF } =
    useFlags();

  const handleOpenWidget = () => {
    getWidgetData(socialEcommFF, disableAvatarsFF);
    setWidgetOpen(true);
  };

  const getSortedRedeemedMembers = () => {
    if (redeemedMembers.length) {
      const self = find(redeemedMembers, (member) => member.userId === userId);
      const captain = find(
        redeemedMembers,
        (member) => member.userId === captainId
      );
      return uniqBy(
        [self, captain, ...redeemedMembers],
        (member) => member && member.userId
      );
    }
    return [];
  };

  const sortedRedeemedMembers = useMemo(
    () => getSortedRedeemedMembers(),
    [redeemedMembers, captainId, userId]
  );

  const mapSocialAvatars = () => {
    const userIdToAvatar = {};
    for (let i = 0; i < socialAvatars.length; i += 1) {
      userIdToAvatar[socialAvatars[i].userId] = socialAvatars[i].userAvatarUrl;
    }
    return userIdToAvatar;
  };

  const handleClickOutside = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setWidgetOpen(false);
    }
  };

  const handleWidgetViewedTracking = () => {
    track('Friend Widget Viewed', {
      ...trackingData,
      widget_tab: 'Team List',
    });
  };

  useEffect(() => {
    if (viewSize === VIEW_SIZE.small && itemsContainerRef.current) {
      if (widgetOpen) {
        disableBodyScroll(itemsContainerRef.current);
      } else enableBodyScroll(itemsContainerRef.current);
    }
    return () => clearAllBodyScrollLocks();
  }, [viewSize, widgetOpen, itemsContainerRef]);

  useEffect(() => {
    if (widgetOpen) {
      document.addEventListener('click', handleClickOutside, false);
      handleWidgetViewedTracking();
    }
    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, [widgetOpen]);

  return (
    <>
      <button
        type="button"
        onClick={handleOpenWidget}
        className={classNames(styles.icon, {
          [styles.bannerShowing]: !socialBannerIsInView,
          [styles.hiddenButton]: widgetOpen,
        })}
      >
        <i className="far fa-users" />
      </button>
      {widgetOpen && (
        <div
          className={classNames(styles.container, {
            [styles.bannerShowing]: !socialBannerIsInView,
          })}
          ref={containerRef}
        >
          <div className={styles.navBar}>
            <span>
              My Friends
              {redeemedMembers.length > 0 && ` (${redeemedMembers.length})`}
            </span>
            <button
              type="button"
              onClick={() => setWidgetOpen(false)}
              className="naked-button"
            >
              <i className="fal fa-times" />
            </button>
          </div>
          <div className={styles.membersContainer} ref={itemsContainerRef}>
            <WidgetSection
              members={sortedRedeemedMembers}
              userIdToAvatar={mapSocialAvatars()}
              userId={userId}
              captainId={captainId}
            />
          </div>
          <Button
            onClick={() => {
              trackInviteStarted(trackingData, 'Friend Widget', 'SEND INVITE');
              openInviteModal(btnLocationEnum.friendWidget);
            }}
            disabled={loading}
          >
            Send Invite
          </Button>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  userId: state.userState.userId,
  redeemedMembers: state.socialEcommState.redeemedMembers,
  socialAvatars: state.socialEcommState.socialAvatars,
  captainId: state.socialEcommState.team.captainShopUserId,
  loading: state.socialEcommState.fetchingInvitationsAndTeam,
  trackingData: selectPageTrackingFullData(state),
});

const mapDispatchToProps = {
  getWidgetData: requestWidgetData,
};

TeamWidget.propTypes = {
  userId: PropTypes.number.isRequired,
  viewSize: PropTypes.string.isRequired,
  redeemedMembers: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      invitedByName: PropTypes.string,
    })
  ).isRequired,
  socialAvatars: PropTypes.arrayOf(
    PropTypes.shape({
      userAvatarUrl: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      userId: PropTypes.number.isRequired,
    })
  ).isRequired,
  captainId: PropTypes.number,
  loading: PropTypes.bool.isRequired,
  trackingData: PropTypes.shape().isRequired,
  getWidgetData: PropTypes.func.isRequired,
  socialBannerIsInView: PropTypes.bool.isRequired,
  openInviteModal: PropTypes.func.isRequired,
};

TeamWidget.defaultProps = {
  captainId: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(TeamWidget);
